import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import { MedicalServices as MedNecIcon, Add as AddIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { PageContainer, PageCard, DashboardHeader, StatusPage } from '../../../common/components';
import { useGetCenterQuery } from '../../../../services/ConfigurationService';
import { useMedNecContext } from '../../hooks/useMedNecContext';
import { useCaseIntakeForm } from '../../hooks/useCaseIntakeForm';
import {
  PatientSection,
  PhysicianSection,
} from '../sections';
import CaseFormTabs from '../CaseFormTabs';

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5, display: 'block' }}>
    {children}
  </Typography>
);

export const MedNecCaseCreate: React.FC = () => {
  const navigate = useNavigate();
  const { company, center, centerId, isConfigured } = useMedNecContext();

  // Center lookup for display name
  const { data: centerData } = useGetCenterQuery(
    { id: centerId, company },
    { skip: !isConfigured },
  );
  const centerDisplayName = centerData?.shortName || centerData?.name || center;

  const {
    errors, isSubmitting, isSuccess, createdCase, submitError,
    handleSubmit, reset,
  } = useCaseIntakeForm(center);

  const goBack = () => {
    const params = new URLSearchParams();
    if (company) params.append('company', company);
    if (center) params.append('center', center);
    navigate(`/mednec?${params.toString()}`);
  };

  // Guard: missing config
  if (!isConfigured) {
    return (
      <PageContainer>
        <StatusPage
          variant="configuration-required"
          icon={MedNecIcon}
          title="Configuration Required"
          message="Company and center must be configured before creating a case."
          chips={[
            { label: `Company: ${company || 'Not set'}`, color: company ? 'success' : 'warning' },
            { label: `Center: ${center || 'Not set'}`, color: center ? 'success' : 'warning' },
          ]}
        />
      </PageContainer>
    );
  }

  // Success — navigate directly to the case detail page
  if (isSuccess && createdCase) {
    const detailParams = new URLSearchParams();
    detailParams.append('id', createdCase.id);
    if (company) detailParams.append('company', company);
    if (center) detailParams.append('center', center);
    navigate(`/mednec/cases/edit?${detailParams.toString()}`, { replace: true });
    return null;
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          minWidth: '1000px',
          maxWidth: '1100px',
          width: '100%',
        }}>
          <DashboardHeader
            icon={MedNecIcon}
            title="New Medical Necessity Case"
            subtitle={`${company} — ${centerDisplayName}`}
            stats={[]}
            actions={[
              { label: 'Back', icon: BackIcon, onClick: goBack, variant: 'outlined' },
              { label: isSubmitting ? 'Creating...' : 'Create Case', icon: AddIcon, onClick: handleSubmit, disabled: isSubmitting },
            ]}
          />

          <Box sx={{ p: 3 }}>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {'data' in submitError
                  ? String((submitError as { data: unknown }).data)
                  : 'An error occurred creating the case. Please try again.'}
              </Alert>
            )}

            <Label>Patient Demographics</Label>
            <PatientSection mode="create" errors={errors} />

            <Label>Physician</Label>
            <PhysicianSection mode="create" company={company} centerId={centerId} errors={errors} />

            <CaseFormTabs mode="create" errors={errors} company={company} centerId={centerId} />
          </Box>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default MedNecCaseCreate;
